import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Collapse, IconButton, Skeleton, Tooltip, Typography } from '@mui/material';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import AddIcon from '@mui/icons-material/Add';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LinkIcon from '@mui/icons-material/Link';
import InfoNotice from '../../Components/InfoNotice';
import PanelEmptyState from '../../Components/PanelEmptyState';
import { toast } from '../../Components/Toast';
import { confirmDialog } from '../../Components/ConfirmDialog';
import { BlockCollapseToggle, BlockHeader, PRIVATE_BLOCK_SX } from '../../Map/components/DockPanel/Tabs/formBlockUi';
import { getUnitCampaigns, linkAdUnit, unlinkCampaignUnit } from '../../services/metaAdsService';
import { getMetaErrorMessage, readMetaErrorCode } from '../metaAdsConfig';
import { describeMetaLinkedBy } from '../metaCampaigns';
import { useMetaAdsContext } from '../MetaAdsContext';
import { useMetaConnect } from '../hooks/useMetaConnect';
import { formatMetaDateTime } from '../metaPeriod';
import {
  describeNewAdMeta,
  describeUnitCampaignsPeriod,
  describeUnitCampaignsSummary,
  groupUnitAdsByCampaign,
  metaMetricsSummary,
  metaRecordNoun,
  readUnitCampaigns,
  unitCampaignsAccess,
  unitSyncNotice,
  unitCampaignsPeriod,
  unitTotalsRows,
} from '../metaUnitCampaigns';
import MetaAdThumb from './MetaAdThumb';
import MetaAdPreviewDialog from './MetaAdPreviewDialog';
import UnitReportDialog from './UnitReportDialog';
import MetaLinkCampaignDialog from './MetaLinkCampaignDialog';
import MetaCreateCampaignDialog from './MetaCreateCampaignDialog';
import { metaCreateAvailable } from '../metaCreate';
import { LinkMenu } from './MetaActionMenu';
import { MetaTooltipBoundaryContext, metaTooltipBoundaryProps } from './metaTooltipBoundary';
import MetaContactsHelp from './MetaContactsHelp';
import MetaContactsTooltip from './MetaContactsTooltip';
import { metaContactsNote, metaContactsShortNote } from '../metaContacts';
import { MetaActiveDot, MetaDeliveryDot, MetaDeliveryPill, isMetaDeliveryActive, metaDeliveryShowsStatus } from './MetaDeliveryBits';
// Recomendaciones de Meta ocultas por decisión de producto (28/10/2026): se harán desde Terrio más adelante. Descomentar para reactivar.
// import { MetaRecommendations } from './MetaDeliveryBits';
// import { collectMetaRecommendations } from '../metaDelivery';
import {
  getMetaAdsStatusPillSx, metaAdsBodyTextSx, metaAdsContainedDisabledSx, metaAdsMutedTextSx, metaAdsSkeletonSx,
} from './metaAdsUi.styles';
import { colors } from '../../styles/theme';

export const RECORD_CAMPAIGNS_TITLE = 'Campañas';

const textButtonSx = { textTransform: 'none', fontWeight: 600, px: 1, minWidth: 0, fontSize: '12px' };
const rowSx = { py: 1, borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', gap: 0.5 };
// Bloque de cada campaña (t995u): 16 px entre el resumen (o la campaña anterior) y el divisor, y 12 px
// entre el divisor y el título (escala del tema: 8 de gap + mt 1; pt 1.5).
const campaignRowSx = { ...rowSx, gap: 0.75, mt: 1, pt: 1.5 };
// Último anuncio → divisor: 8 (pb de la campaña) + 8 (gap) − 4 = 12 px; divisor → botón: 12 px.
// t999u: aire del pie a la mitad aprox: 6 px sobre el divisor (antes 12), 6 px divisor → botón (antes
// 12) y 6 px menos hasta el borde de la tarjeta (mb negativo contra su padding de 16).
const footerSx = { display: 'flex', alignItems: 'center', justifyContent: 'center', borderTop: rowSx.borderTop, mt: -1.25, pt: 0.75, mb: -0.75 };
// Anuncio dentro de su campaña: miniatura | nombre (más chico) y acciones. Con fase 1, miniatura
// de 52 px (antes 40) y columna centrada con ella.
const RECORD_AD_THUMB = 40;
// t995u: miniatura de la ficha más grande (52 → 72 px); el panel Campañas usa su propio tamaño.
const RECORD_AD_THUMB_FASE1 = 72;
const unitAdSx = (thumb) => ({ display: 'grid', gridTemplateColumns: `${thumb}px minmax(0, 1fr)`, columnGap: thumb > RECORD_AD_THUMB ? 1.25 : 1, alignItems: thumb > RECORD_AD_THUMB ? 'center' : 'start' });
// ⋮ de la campaña en la ficha (mismo menú que Historial / Campañas).
const campaignMenuIconSx = { fontSize: 20 };
const CAMPAIGN_MENU_ICONS = {
  'open-campaign': <CampaignOutlinedIcon sx={campaignMenuIconSx} />,
  'unlink-campaign': <LinkOffIcon sx={campaignMenuIconSx} />,
};
// Título de la campaña como botón (t994u): mismo texto que antes (no link azul), subrayado al hover.
const campaignTitleButtonSx = {
  ...metaAdsBodyTextSx,
  fontWeight: 600,
  overflowWrap: 'anywhere',
  display: 'block',
  width: '100%',
  textAlign: 'left',
  p: 0,
  m: 0,
  border: 0,
  background: 'none',
  color: 'inherit',
  cursor: 'pointer',
  '&:hover': { textDecoration: 'underline' },
  '&:focus-visible': { outline: `2px solid ${colors.primaryLight}`, outlineOffset: 2, borderRadius: '2px' },
};
const linkCampaignButtonSx = { ...textButtonSx, fontSize: '13px', color: colors.primaryLight, '& .MuiButton-startIcon': { mr: 0.5 } };
// «Ver informe» (29/10): ícono de Informe (el mismo del panel Campañas), color primario, con Tooltip,
// pegado al texto del período (gap de la fila, 4px). 16px + 2px de padding = 20px: no agranda la fila.
const reportIconButtonSx = {
  p: 0.25, my: -0.25, flexShrink: 0, color: colors.primaryLight,
  '&:hover': { bgcolor: 'rgba(72,146,145,0.12)', color: colors.primaryLight },
};
const adNameSx = { ...metaAdsMutedTextSx, fontSize: '12px', lineHeight: '16px', color: 'rgba(255,255,255,0.78)', overflowWrap: 'anywhere' };

/** Mismo criterio que «Consultas y leads»: 401 / 403 / 404 (y entorno sin migrar) ocultan la sección. */
const isHiddenResponse = (res) => {
  const status = res?._httpStatus;
  return status === 401 || status === 403 || status === 404 || readMetaErrorCode(res) === 'META_ADS_NOT_MIGRATED';
};

const sameOwner = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase();

/**
 * Nombre del anuncio (más chico, debajo de la campaña). Si la API manda quién vinculó, va
 * en el Tooltip de MUI del nombre (sin el chip «Por anuncio» / «Por campaña»; nunca `title`).
 */
function AdName({ ad, linkedByContext, tooltipProps }) {
  const who = describeMetaLinkedBy(ad, linkedByContext);
  const when = who ? formatMetaDateTime(ad.linkedAt) : '';
  const by = when ? `${who} · ${when}` : who;
  const name = (
    <Typography
      component="span"
      tabIndex={by ? 0 : undefined}
      sx={{ ...adNameSx, display: 'block', minWidth: 0 }}
      data-testid={`record-campaigns-ad-${ad.id}-name`}
    >
      {ad.name}
    </Typography>
  );
  return by ? <Tooltip title={<span>{by}</span>} placement="top" arrow describeChild {...tooltipProps}>{name}</Tooltip> : name;
}

/**
 * Dueño sin Meta conectado (o sin cuentas, o desconectado / con conexión vencida sin anuncios en
 * esta ficha): una sola línea. `reconnect` (expired / error) → «Reconectá Meta…».
 */
function ConnectLine({ noun, connected, reconnect = false, onOpen }) {
  return (
    <Box data-testid="record-campaigns-connect" sx={{ ...PRIVATE_BLOCK_SX, px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <CampaignOutlinedIcon sx={{ fontSize: 18, color: '#ABABAB' }} aria-hidden />
      <Typography sx={{ ...metaAdsMutedTextSx, flex: 1, minWidth: 0 }}>
        {reconnect
          ? `Reconectá Meta para ver qué anuncios promocionan ${noun.this}.`
          : connected
          ? `Elegí una cuenta publicitaria para ver qué anuncios promocionan ${noun.this}.`
          : `Conectá Meta para ver qué anuncios promocionan ${noun.this}.`}
      </Typography>
      <Button size="small" onClick={onOpen} sx={textButtonSx}>Abrir anuncios</Button>
    </Box>
  );
}

/**
 * «Campañas» de un registro (E4, `GET /getunitcampaigns`). Mismo patrón que
 * «Consultas y leads» (`RecordLeadsSection`): colapsable, se pide recién cuando
 * entra en pantalla, se cancela al cambiar de registro (el padre le pone `key`
 * por registro) y se oculta sola con 401 / 403 / 404.
 *
 * Necesita el `MetaAdsProvider` de MapPage (flag prendido y la API visible);
 * sin provider (shares, páginas compartidas) no existe. Se monta donde se monta
 * «Consultas y leads», con las mismas condiciones.
 *
 * @param {{ itemKind: 'user_lot'|'cadastral_lot'|'cad_parcel', recordId: string, ownerUserId?: string|null,
 *   projectId?: string|null }} props `projectId`: showroom de la parcela (Crear campaña lo comparte si hace falta).
 */
export default function RecordCampaignsSection({ itemKind = 'user_lot', recordId, ownerUserId = null, projectId = null }) {
  const meta = useMetaAdsContext();
  const metaConnection = meta?.metaConnection || null;
  // F2a: cambia después de una acción en Meta desde el panel → la sección se vuelve a pedir.
  const campaignsVersion = meta?.campaignsVersion || 0;
  const connection = metaConnection?.connection || null;
  const rootRef = useRef(null);
  const abortRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState('idle');
  const [state, setState] = useState(null);
  const [pending, setPending] = useState(() => new Set());
  const [reloadKey, setReloadKey] = useState(0);
  // «Ver anuncio» (ajuste 12): el anuncio abierto; la URL vive solo dentro del diálogo.
  const [preview, setPreview] = useState(null);
  // «Ver informe» (fase 1): diálogo abierto o no.
  const [reportOpen, setReportOpen] = useState(false);
  // «Vincular campaña»: el selector se monta al abrirlo la primera vez (getmetacampaigns recién
  // ahí) y queda montado para reusar la lista si se vuelve a abrir en la misma ficha.
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkMounted, setLinkMounted] = useState(false);
  // «Crear campaña» (F2b API): el diálogo se monta al abrirlo.
  const [createOpen, setCreateOpen] = useState(false);
  // Mismo período que el panel por defecto («Últimos 30 días», sin hoy): los números coinciden.
  const requestedPeriod = useMemo(() => unitCampaignsPeriod(), []);
  // Límite de los Tooltip del bloque (no se cortan contra el borde del panel).
  const [boundary, setBoundary] = useState(null);
  const setRoot = useCallback((node) => {
    rootRef.current = node;
    setBoundary(node);
  }, []);
  const tooltipProps = useMemo(() => metaTooltipBoundaryProps(boundary), [boundary]);
  // «Reconectar» del historial: mismo flujo que el panel (live → Meta y vuelve a /#/meta-ads;
  // demo → POST connectmeta y se aplica la conexión, que vuelve a `ready`).
  const { connecting, connect } = useMetaConnect({ onConnected: metaConnection?.applyConnectionData });

  // La conexión del contexto es la del espacio activo: si la ficha es de otro espacio, no aplica.
  const available = Boolean(metaConnection?.loaded && !metaConnection.hidden && !metaConnection.error && connection
    && sameOwner(ownerUserId, metaConnection.ownerUserId));
  const access = available ? unitCampaignsAccess(connection) : 'none';
  const ready = access === 'ready';
  // Desconectado sin purge: se pide igual (la API conserva el historial), sin carga diferida.
  const history = access === 'history';
  // Conexión vencida o con error (needs_reconnect): getunitcampaigns no mira la conexión (Ajuste A).
  const reconnect = access === 'reconnect';
  // Historial congelado (desconectado o por reconectar): se pide sin carga diferida y es solo lectura.
  const frozen = history || reconnect;
  const shouldLoad = ready || frozen;
  // Con Meta desconectado la ficha queda en solo lectura (ver docs: decisión).
  const canLink = ready && connection.viewerCanLink;
  // Solo el dueño, en su espacio, con `create_campaigns: available` (si no, no hay botón).
  const canCreate = ready && metaCreateAvailable({ connection, ownerUserId, itemKind });
  const noun = metaRecordNoun(itemKind);

  useEffect(() => {
    if (visible || !ready) return undefined;
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) setVisible(true);
    }, { rootMargin: '200px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible, ready]);

  useEffect(() => {
    if (!(visible || frozen) || !shouldLoad || !recordId) return undefined;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus((prev) => (prev === 'ready' ? prev : 'loading'));
    getUnitCampaigns({ itemKind, id: recordId, period: requestedPeriod, ownerUserId, signal: controller.signal })
      .then((res) => {
        if (controller.signal.aborted) return;
        if (!res?._httpOk) {
          if (!isHiddenResponse(res)) console.error('Campañas: getunitcampaigns falló', res?._httpStatus, readMetaErrorCode(res));
          setState(null);
          setStatus('hidden');
          return;
        }
        setState(readUnitCampaigns(res));
        setStatus('ready');
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        setState(null);
        setStatus('hidden');
      });
    return () => controller.abort();
  }, [visible, frozen, shouldLoad, itemKind, recordId, ownerUserId, reloadKey, requestedPeriod, campaignsVersion]);

  const record = { itemKind, id: recordId };
  // Después de vincular / desvincular: `invalidateCampaigns` sube `campaignsVersion` (esta ficha y
  // el panel de Campañas recargan en silencio); sin él (tests / sin MapPage), recarga local.
  const invalidateCampaigns = meta?.invalidateCampaigns;
  const afterChange = useCallback(() => {
    if (invalidateCampaigns) invalidateCampaigns();
    else setReloadKey((k) => k + 1);
    metaConnection?.refresh?.();
  }, [metaConnection, invalidateCampaigns]);

  const withPending = async (key, fn) => {
    if (pending.has(key)) return;
    setPending((prev) => new Set(prev).add(key));
    try {
      await fn();
    } finally {
      setPending((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleSuggest = (ad) => withPending(`new:${ad.id}`, async () => {
    const res = await linkAdUnit({ adId: ad.id, records: [record], ownerUserId });
    if (res?._httpOk) {
      toast.success({ message: `Sumamos «${ad.name}» a ${noun.this}.` });
      afterChange();
      return;
    }
    toast.error({ title: 'No pudimos vincular el anuncio', message: getMetaErrorMessage(res) });
    if (readMetaErrorCode(res) === 'META_AD_NOT_FOUND') setReloadKey((k) => k + 1);
  });

  /**
   * «Desvincular campaña de esta unidad»: SIEMPRE con este registro en `records` (sin `records`
   * la API desvincula la campaña de TODAS sus unidades). Sin registro o sin campaña, no llama.
   */
  const handleUnlinkCampaign = (group) => {
    const campaignId = group?.campaignId;
    if (!campaignId || !recordId || !itemKind) return Promise.resolve();
    return withPending(`campaign:${campaignId}`, async () => {
      const name = group.campaignName || 'la campaña';
      const result = await confirmDialog({
        title: `¿Desvincular la campaña de ${noun.this}?`,
        description: `«${name}» deja de contar para ${noun.this}. Las otras unidades no cambian y no se borran métricas.`,
        confirmLabel: 'Desvincular',
        variant: 'danger',
      });
      if (!result?.isConfirmed) return;
      const snapshot = state;
      setState((prev) => (prev ? { ...prev, ads: prev.ads.filter((a) => a.campaignId !== campaignId) } : prev));
      const res = await unlinkCampaignUnit({ campaignId, records: [{ itemKind, id: recordId }], ownerUserId });
      if (res?._httpOk) {
        toast.success({ message: `Desvinculaste «${name}» de ${noun.this}.` });
        afterChange();
        return;
      }
      setState(snapshot);
      toast.error({ title: 'No pudimos desvincular la campaña', message: getMetaErrorMessage(res) });
      if (readMetaErrorCode(res) === 'META_CAMPAIGN_NOT_FOUND') setReloadKey((k) => k + 1);
    });
  };

  const openLinkDialog = () => {
    setLinkMounted(true);
    setLinkOpen(true);
  };
  const handleCampaignLinked = (campaign, res) => {
    setLinkOpen(false);
    const ads = Number(res?.ads_count) || 0;
    toast.success({
      message: ads > 0
        ? `Vinculaste ${ads} ${ads === 1 ? 'anuncio' : 'anuncios'} de «${campaign.name}» con ${noun.this}.`
        : `Vinculaste «${campaign.name}» con ${noun.this}.`,
    });
    setExpanded(true);
    afterChange();
  };

  const handleCreateChanged = () => {
    setExpanded(true);
    afterChange();
  };

  if (!available) return null;
  // Solo el dueño puede conectar / elegir cuentas: al editor no se le muestra nada.
  const connectLine = connection.viewerCanManage
    ? <ConnectLine noun={noun} connected={connection.connected} reconnect={reconnect} onOpen={() => meta.openPanel?.()} />
    : null;
  if (access === 'connect' || access === 'none') return connectLine;
  if (frozen) {
    // Hasta saber si hay historial no se pinta nada (evita línea → sección). Los datos de
    // getunitcampaigns deciden: sin anuncios o con error, la línea (dueño) o nada (editor).
    if (status !== 'ready' || !state) return status === 'hidden' ? connectLine : null;
    if (state.ads.length === 0) return connectLine;
  }
  if (status === 'hidden') return null;

  const isReady = status === 'ready' && state;
  const lastData = formatMetaDateTime(connection.lastSyncAt);
  const summary = isReady ? describeUnitCampaignsSummary(state) : 'Cargando…';
  const subtitle = history ? `${summary} · Meta desconectado` : reconnect ? `${summary} · Reconectá Meta` : summary;
  const notice = frozen ? null : unitSyncNotice(connection);
  const period = isReady ? describeUnitCampaignsPeriod(state.period, requestedPeriod) : '';
  const groups = isReady ? groupUnitAdsByCampaign(state.ads) : [];
  // Fase 1 (misma señal que «Ver informe»): la ficha se ordena como el panel de Campañas (línea
  // corta con ⓘ, sin repetir la línea con una sola campaña, punto verde en vez de «Activa»,
  // miniatura más grande, «Ir a Campañas» al pie). Sin fase 1 queda como antes.
  const fase1 = isReady && state.budgetAvailable === true;
  const totals = isReady ? unitTotalsRows(state.totals, { short: fase1 }) : null;
  const summaryOf = (ad) => metaMetricsSummary(ad.metrics, ad.currency, { short: fase1 }) || 'Sin datos en el período';
  const noteOf = (ad) => (fase1 ? metaContactsShortNote(ad.metrics) : metaContactsNote(ad.metrics));
  // Una sola campaña con un solo anuncio y la misma línea que el total: no se repite (mismo texto =
  // mismos datos; si difiere en algo, se muestra).
  const onlyAd = fase1 && groups.length === 1 && groups[0].ads.length === 1 ? groups[0].ads[0] : null;
  const hideSingleLine = Boolean(onlyAd && !totals.mixed && totals.rows.length === 1 && totals.rows[0].text === summaryOf(onlyAd));
  const anyShared = isReady && state.ads.some((a) => a.isShared);
  // t994u: sin «Ir a Campañas» / «Ver todas las campañas» (al panel se llega desde el sidebar). El
  // título de cada campaña abre el panel Campañas con esa campaña elegida (antes, el ⋮ «Ver en Campañas»).
  const openCampaign = meta.openPanel ? (campaignId) => meta.openPanel('campaigns', { campaignId }) : null;
  const linkedByContext = { viewerUserId: meta.viewerUserId || null, ownerUserId };
  const showReport = isReady && state.ads.length > 0 && state.budgetAvailable === true;
  const collapseToggle = (
    <BlockCollapseToggle
      expanded={expanded}
      onToggle={() => setExpanded((v) => !v)}
      labelExpanded="Contraer campañas"
      labelCollapsed="Expandir campañas"
    />
  );

  return (
    <MetaTooltipBoundaryContext.Provider value={boundary}>
      <Box ref={setRoot} data-testid="record-campaigns-section" sx={{ ...PRIVATE_BLOCK_SX, p: 2 }}>
        <BlockHeader
          icon={<CampaignOutlinedIcon sx={{ fontSize: 18, color: '#ABABAB', mt: 0.1 }} />}
          title={RECORD_CAMPAIGNS_TITLE}
          subtitle={subtitle}
          action={collapseToggle}
        />
        <Collapse in={expanded} unmountOnExit>
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {!isReady ? (
              <>
                <Skeleton variant="rounded" height={44} sx={metaAdsSkeletonSx} />
                <Skeleton variant="rounded" height={44} sx={metaAdsSkeletonSx} />
              </>
            ) : null}

            {isReady && history ? (
              <InfoNotice
                title={lastData ? `Meta desconectado, datos hasta el ${lastData}` : 'Meta desconectado'}
                description={connection.viewerCanManage
                  ? 'Reconectalo para volver a traer resultados y vincular anuncios.'
                  : 'El dueño del espacio tiene que reconectarlo para traer resultados nuevos.'}
                actionLabel={connection.viewerCanManage ? 'Reconectar' : ''}
                onAction={connection.viewerCanManage ? connect : undefined}
                actionDisabled={connecting}
                data-testid="record-campaigns-disconnected"
              />
            ) : null}

            {isReady && reconnect ? (
              <InfoNotice
                title="Reconectá Meta"
                description={`${lastData ? `Datos hasta el ${lastData}. ` : ''}${connection.viewerCanManage
                  ? 'La conexión venció o falló; reconectala para volver a traer resultados y vincular anuncios.'
                  : 'La conexión venció o falló; el dueño del espacio tiene que reconectarla.'}`}
                actionLabel={connection.viewerCanManage ? 'Reconectar' : ''}
                onAction={connection.viewerCanManage ? connect : undefined}
                actionDisabled={connecting}
                data-testid="record-campaigns-reconnect"
              />
            ) : null}

            {isReady && notice ? (
              <InfoNotice
                title={notice.title}
                description={lastData ? `Datos hasta el ${lastData}.` : 'Puede que los datos no estén al día.'}
                data-testid="record-campaigns-sync-notice"
              />
            ) : null}

            {isReady && state.ads.length > 0 ? (
              <Box data-testid="record-campaigns-totals" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 20 }}>
                  {period ? <Typography sx={metaAdsMutedTextSx} data-testid="record-campaigns-period">{period}</Typography> : null}
                  {/* Fase 1: el ⓘ va al final de la línea de totales (con el desglose). */}
                  {fase1 ? null : <MetaContactsHelp testId="record-campaigns-contacts-help" />}
                  {showReport ? (
                    <Tooltip title={<span>Ver informe</span>} placement="top" arrow {...tooltipProps}>
                      <IconButton
                        size="small"
                        aria-label="Ver informe"
                        onClick={() => setReportOpen(true)}
                        data-testid="record-campaigns-report-open"
                        sx={reportIconButtonSx}
                      >
                        <DescriptionOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Box>
                {fase1 ? (
                  // Fase 1: una línea por moneda, corta; un solo ⓘ al final del bloque (último renglón)
                  // con el desglose de contactos y qué cuenta como contacto. Sin Tooltip en la línea (no duplica).
                  <>
                    {totals.rows.map((row, i) => {
                      const last = i === totals.rows.length - 1 && !(totals.mixed && totals.contactsText);
                      return (
                        <Typography key={row.currency || 'total'} sx={{ ...metaAdsBodyTextSx, fontWeight: 600 }} data-testid={`record-campaigns-totals-row-${row.currency || 'total'}`}>
                          {row.text}
                          {last ? <MetaContactsHelp testId="record-campaigns-contacts-help" note={row.note} sx={{ ml: 0.5, verticalAlign: 'middle' }} /> : null}
                        </Typography>
                      );
                    })}
                    {totals.mixed && totals.contactsText ? (
                      <Typography sx={metaAdsMutedTextSx} data-testid="record-campaigns-totals-contacts">
                        {`En total: ${totals.contactsText}. Los montos van por moneda, sin sumarlos.`}
                        <MetaContactsHelp testId="record-campaigns-contacts-help" note={totals.contactsNote} sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                      </Typography>
                    ) : null}
                  </>
                ) : (
                  <>
                    {totals.rows.map((row) => (
                      <MetaContactsTooltip key={row.currency || 'total'} note={row.note}>
                        <Typography sx={{ ...metaAdsBodyTextSx, fontWeight: 600 }} data-testid={`record-campaigns-totals-row-${row.currency || 'total'}`}>
                          {row.text}
                        </Typography>
                      </MetaContactsTooltip>
                    ))}
                    {totals.mixed && totals.contactsText ? (
                      <MetaContactsTooltip note={totals.contactsNote}>
                        <Typography sx={metaAdsMutedTextSx} data-testid="record-campaigns-totals-contacts">
                          {`En total: ${totals.contactsText}. Los montos van por moneda, sin sumarlos.`}
                        </Typography>
                      </MetaContactsTooltip>
                    ) : null}
                  </>
                )}
                {anyShared ? (
                  <Typography sx={metaAdsMutedTextSx} data-testid="record-campaigns-shared-note">
                    Los anuncios compartidos cuentan completos (no se divide su costo).{state.budgetAvailable === true ? ' En el informe, el gasto de los anuncios compartidos se reparte en partes iguales.' : ''}
                  </Typography>
                ) : null}
              </Box>
            ) : null}

            {isReady && !frozen && state.unlinkedNewAds.length > 0 ? (
              <Box component="ul" data-testid="record-campaigns-new" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {state.unlinkedNewAds.map((ad) => (
                  <Box component="li" key={ad.id} data-testid={`record-campaigns-new-${ad.id}`} sx={rowSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        <MetaDeliveryDot delivery={ad.delivery} testId={`record-campaigns-new-${ad.id}-dot`} />
                        <Typography sx={{ ...metaAdsBodyTextSx, fontWeight: 600, overflowWrap: 'anywhere' }}>{ad.name}</Typography>
                      </Box>
                      <Box component="span" sx={getMetaAdsStatusPillSx('warning')}>Anuncio nuevo</Box>
                    </Box>
                    {describeNewAdMeta(ad) ? (
                      <Typography data-testid={`record-campaigns-new-${ad.id}-meta`} sx={metaAdsMutedTextSx}>{describeNewAdMeta(ad)}</Typography>
                    ) : null}
                    <Typography sx={metaAdsMutedTextSx}>
                      {`Nuevo en «${ad.campaignName || 'la campaña'}», vinculada entera a ${noun.this}.`}
                    </Typography>
                    {canLink ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Button size="small" variant="contained" disableElevation disabled={pending.has(`new:${ad.id}`)}
                          onClick={() => handleSuggest(ad)} sx={{ ...textButtonSx, px: 1.5, ...metaAdsContainedDisabledSx }}>
                          Sumarlo a {noun.this}
                        </Button>
                      </Box>
                    ) : null}
                  </Box>
                ))}
              </Box>
            ) : null}

            {isReady && state.ads.length === 0 ? (
              <PanelEmptyState
                icon={CampaignOutlinedIcon}
                title={`Ningún anuncio promociona ${noun.this} todavía`}
                testId="record-campaigns-empty"
                sx={{ minHeight: 0, py: 2 }}
              />
            ) : null}
            {isReady && state.ads.length === 0 && (canLink || canCreate) ? (
              // Vacía y con permiso: «Vincular campaña» (selector acá mismo). Con «Crear campaña»
              // (F2b API), crear es el principal y vincular queda como texto al lado.
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: -1, flexWrap: 'wrap' }}>
                {canCreate ? (
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                    onClick={() => setCreateOpen(true)}
                    data-testid="record-campaigns-empty-create"
                    sx={{ ...textButtonSx, fontSize: '13px', px: 1.5, ...metaAdsContainedDisabledSx }}
                  >
                    Crear campaña
                  </Button>
                ) : null}
                {canLink ? (
                  <Button
                    size="small"
                    variant={canCreate ? 'text' : 'contained'}
                    disableElevation
                    startIcon={canCreate ? <LinkIcon sx={{ fontSize: 18 }} /> : <AddIcon sx={{ fontSize: 18 }} />}
                    onClick={openLinkDialog}
                    data-testid="record-campaigns-empty-link"
                    sx={canCreate ? linkCampaignButtonSx : { ...textButtonSx, fontSize: '13px', px: 1.5, ...metaAdsContainedDisabledSx }}
                  >
                    Vincular campaña
                  </Button>
                ) : null}
              </Box>
            ) : null}

            {isReady && state.ads.length > 0 ? (
              // Se lee primero la campaña (título) y abajo sus anuncios, más chicos.
              <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {groups.map((group) => {
                  const single = group.ads.length === 1 ? group.ads[0] : null;
                  return (
                    <Box
                      component="li"
                      key={group.key}
                      data-testid={`record-campaigns-campaign-${group.campaignId || group.ads[0].id}`}
                      sx={campaignRowSx}
                    >
                      {/* Título y ⋮ centrados en la misma fila (t995u). */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }} data-testid={`record-campaigns-campaign-${group.campaignId || group.ads[0].id}-head`}>
                        {group.campaignId && openCampaign ? (
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Tooltip title={<span>Ver en Campañas</span>} placement="top" arrow describeChild>
                              <Box
                                component="button"
                                type="button"
                                onClick={() => openCampaign(group.campaignId)}
                                aria-label={`Ver campaña ${group.campaignName || 'sin nombre'} en Campañas`}
                                sx={campaignTitleButtonSx}
                                data-testid={`record-campaigns-campaign-${group.campaignId}-name`}
                              >
                                {group.campaignName || 'Campaña sin nombre'}
                              </Box>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography sx={{ ...metaAdsBodyTextSx, fontWeight: 600, overflowWrap: 'anywhere', flex: 1, minWidth: 0 }} data-testid={`record-campaigns-campaign-${group.campaignId || group.ads[0].id}-name`}>
                            {group.campaignName || 'Campaña sin nombre'}
                          </Typography>
                        )}
                        {group.campaignId && canLink ? (
                          <Box sx={{ flexShrink: 0, mr: '-4px', display: 'flex', alignItems: 'center' }}>
                            <LinkMenu
                              label="Más opciones de la campaña"
                              icons={CAMPAIGN_MENU_ICONS}
                              disabled={pending.has(`campaign:${group.campaignId}`)}
                              testId={`record-campaigns-campaign-${group.campaignId}-menu`}
                              items={[
                                { key: 'unlink-campaign', label: `Desvincular campaña de ${noun.this}`, danger: true, onClick: () => handleUnlinkCampaign(group) },
                              ]}
                            />
                          </Box>
                        ) : null}
                      </Box>
                      {/* Un solo anuncio: los números van una vez, con la campaña. */}
                      {/* Fase 1, una sola campaña con un anuncio: si es igual al total, no se repite. */}
                      {single && !hideSingleLine ? (
                        <MetaContactsTooltip note={noteOf(single)}>
                          <Typography sx={metaAdsMutedTextSx} data-testid={`record-campaigns-ad-${single.id}-metrics`}>
                            {summaryOf(single)}
                          </Typography>
                        </MetaContactsTooltip>
                      ) : null}
                      {group.ads.map((ad) => {
                        // Fase 1: «Activa» = punto verde con Tooltip (como el panel); otros estados, su píldora.
                        const activeDot = fase1 && isMetaDeliveryActive(ad.delivery);
                        // Sin entrega reciente: punto verde + «Sin mostrarse hoy ni ayer» (no la píldora).
                        const showStatus = !activeDot || metaDeliveryShowsStatus(ad.delivery);
                        const thumb = fase1 ? RECORD_AD_THUMB_FASE1 : RECORD_AD_THUMB;
                        // Sin «Desvincular» por anuncio (29/10): se desvincula la campaña desde su ⋮. La fila
                        // de píldoras solo va si hay algo que mostrar (estado no activo o «compartido»).
                        const showActions = showStatus || ad.isShared;
                        return (
                        <Box key={ad.id} data-testid={`record-campaigns-ad-${ad.id}`} sx={unitAdSx(thumb)}>
                          <MetaAdThumb
                            url={ad.thumbnailUrl}
                            isVideo={ad.isVideo}
                            size={thumb}
                            radius={fase1 ? 8 : 6}
                            onClick={() => setPreview({ ad, campaignName: group.campaignName })}
                            testId={`record-campaigns-ad-${ad.id}-thumb`}
                          />
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0 }}>
                              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: 16, flexShrink: 0 }}>
                                {fase1
                                  ? <MetaActiveDot delivery={ad.delivery} testId={`record-campaigns-ad-${ad.id}-dot`} />
                                  : <MetaDeliveryDot delivery={ad.delivery} testId={`record-campaigns-ad-${ad.id}-dot`} />}
                              </Box>
                              <AdName ad={ad} linkedByContext={linkedByContext} tooltipProps={tooltipProps} />
                            </Box>
                            {!single ? (
                              <MetaContactsTooltip note={noteOf(ad)}>
                                <Typography sx={metaAdsMutedTextSx} data-testid={`record-campaigns-ad-${ad.id}-metrics`}>
                                  {summaryOf(ad)}
                                </Typography>
                              </MetaContactsTooltip>
                            ) : null}
                            {/* Una fila: estado + compartido. «Ver anuncio» = la miniatura. */}
                            {showActions ? (
                            <Box data-testid={`record-campaigns-ad-${ad.id}-actions`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', minHeight: 0 }}>
                              {showStatus ? <MetaDeliveryPill delivery={ad.delivery} label={ad.statusLabel} testId={`record-campaigns-ad-${ad.id}-delivery`} /> : null}
                              {ad.isShared ? <Box component="span" sx={getMetaAdsStatusPillSx('pending')}>{ad.sharedLabel}</Box> : null}
                              {/* Recomendaciones de Meta ocultas por decisión de producto (28/10/2026): se harán desde Terrio más adelante. Descomentar para reactivar.
                              Sin links a Meta en la ficha: como mucho un chip si el anuncio tiene recomendaciones.
                              <MetaRecommendations
                                summary={collectMetaRecommendations([{ label: 'Anuncio', recommendations: ad.recommendations }])}
                                title={ad.name}
                                testId={`record-campaigns-ad-${ad.id}-recommendations`}
                              />
                              */}
                            </Box>
                            ) : null}
                          </Box>
                        </Box>
                        );
                      })}
                    </Box>
                  );
                })}
              </Box>
            ) : null}

            {isReady && !frozen && (canLink || canCreate) && state.ads.length > 0 ? (
              // Pie (t997u): «＋ Vincular campaña» centrado, con el mismo divisor que entre campañas arriba
              // (ancho completo); t999u: menos aire arriba y abajo (ver footerSx). Sin permiso, no hay pie.
              // F2b API: «＋ Crear campaña» al lado (solo el dueño con `create_campaigns: available`).
              <Box sx={{ ...footerSx, gap: 2 }} data-testid="record-campaigns-footer">
                {canLink ? (
                  <Button size="small" startIcon={canCreate ? <LinkIcon sx={{ fontSize: 18 }} /> : <AddIcon sx={{ fontSize: 18 }} />} onClick={openLinkDialog} data-testid="record-campaigns-link-campaign" sx={linkCampaignButtonSx}>
                    Vincular campaña
                  </Button>
                ) : null}
                {canCreate ? (
                  <Button size="small" startIcon={<AddIcon sx={{ fontSize: 18 }} />} onClick={() => setCreateOpen(true)} data-testid="record-campaigns-create-campaign" sx={linkCampaignButtonSx}>
                    Crear campaña
                  </Button>
                ) : null}
              </Box>
            ) : null}
          </Box>
        </Collapse>
        {linkMounted && canLink ? (
          <MetaLinkCampaignDialog
            open={linkOpen}
            record={record}
            recordLabel={state?.recordLabel || ''}
            ownerUserId={ownerUserId}
            onClose={() => setLinkOpen(false)}
            onLinked={handleCampaignLinked}
            onOpenCampaigns={() => { setLinkOpen(false); meta.openPanel?.('campaigns'); }}
          />
        ) : null}
        {createOpen && canCreate ? (
          <MetaCreateCampaignDialog
            open
            itemKind={itemKind}
            recordId={recordId}
            projectId={projectId}
            onClose={() => setCreateOpen(false)}
            onChanged={handleCreateChanged}
          />
        ) : null}
        {reportOpen ? (
          <UnitReportDialog
            itemKind={itemKind}
            recordId={recordId}
            ownerUserId={ownerUserId}
            onClose={() => setReportOpen(false)}
          />
        ) : null}
        {preview ? (
          <MetaAdPreviewDialog
            key={preview.ad.id}
            ad={preview.ad}
            campaignName={preview.campaignName}
            ownerUserId={ownerUserId}
            onClose={() => setPreview(null)}
          />
        ) : null}
      </Box>
    </MetaTooltipBoundaryContext.Provider>
  );
}
